import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Minus,
  Plus,
  ShoppingBag,
  Check,
  ShieldCheck,
  Snowflake,
  Truck,
  FlaskConical,
  ArrowRight,
  ChevronRight,
  AlertCircle,
  Star,
} from 'lucide-react';
import { getProduct, getProducts } from '@/lib/api';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import ProductCard from '@/components/ProductCard';
import SectionHeader from '@/components/SectionHeader';
import type { Product } from '@/types';

const fadeInUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

function PenIllustration({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect x="38" y="12" width="44" height="216" rx="22" fill="#f4f1ea" stroke="#0abab5" strokeWidth="2" />
      <rect x="46" y="28" width="28" height="184" rx="14" fill="#FFFFFF" stroke="#0abab5" strokeWidth="1.5" />
      <rect x="54" y="44" width="12" height="120" rx="6" fill="#4dd4cf" fillOpacity="0.35" />
      <circle cx="60" cy="184" r="10" fill="#0abab5" />
      <rect x="56" y="188" width="8" height="28" rx="4" fill="#0abab5" />
      <rect x="40" y="4" width="40" height="20" rx="10" fill="#0abab5" />
      <text x="60" y="18" textAnchor="middle" fill="#FFFFFF" fontSize="8" fontFamily="IBM Plex Mono, monospace" fontWeight="700">
        AU
      </text>
    </svg>
  );
}

function ProductImage({ product, className = '' }: { product: Product; className?: string }) {
  const src = product.image;

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-rp-bone ${className}`}>
        <PenIllustration className="h-4/5 w-auto opacity-90" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`${product.name} — AURA lyophilized research peptide vial`}
      className={`h-full w-full object-contain p-6 ${className}`}
    />
  );
}

function QuantitySelector({
  quantity,
  onChange,
  max = 10,
}: {
  quantity: number;
  onChange: (q: number) => void;
  max?: number;
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-rp-line bg-rp-paper p-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, quantity - 1))}
        disabled={quantity <= 1}
        className="flex h-10 w-10 items-center justify-center rounded-full text-rp-forest transition-colors hover:bg-rp-bone disabled:opacity-40"
        aria-label="Decrease quantity"
      >
        <Minus size={16} />
      </button>
      <span className="min-w-[2.5ch] text-center font-mono text-sm font-bold text-rp-ink">{quantity}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, quantity + 1))}
        disabled={quantity >= max}
        className="flex h-10 w-10 items-center justify-center rounded-full text-rp-forest transition-colors hover:bg-rp-bone disabled:opacity-40"
        aria-label="Increase quantity"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

function TrustRow() {
  const items = [
    { icon: ShieldCheck, label: 'HPLC-verified' },
    { icon: Snowflake, label: 'Cold-chain' },
    { icon: Truck, label: 'Free worldwide shipping' },
    { icon: FlaskConical, label: 'cGMP / ISO' },
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {items.map(({ icon: Icon, label }) => (
        <span
          key={label}
          className="inline-flex items-center gap-1.5 rounded-full border border-rp-line-2 bg-rp-paper px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-rp-moss"
        >
          <Icon size={12} />
          {label}
        </span>
      ))}
    </div>
  );
}

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const { format } = useCurrencyStore();
  const addItem = useCartStore((s) => s.addItem);

  const {
    data: product,
    isLoading: productLoading,
    error: productError,
  } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => getProduct(slug!),
    enabled: Boolean(slug),
  });

  const category = product?.categorySlug;
  const { data: relatedData, isLoading: relatedLoading } = useQuery({
    queryKey: ['products', category],
    queryFn: () => getProducts(category),
    enabled: Boolean(category),
  });

  const relatedProducts =
    relatedData?.filter((p) => p.id !== product?.id).slice(0, 4) ?? [];

  if (productLoading) {
    return (
      <div className="rp-container py-10 md:py-20">
        <div className="grid gap-8 md:grid-cols-2 lg:gap-16">
          <div className="aspect-square animate-pulse rounded-2xl bg-rp-bone" />
          <div className="space-y-6">
            <div className="h-8 w-3/4 animate-pulse rounded bg-rp-bone" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-rp-bone" />
            <div className="h-12 w-1/3 animate-pulse rounded bg-rp-bone" />
            <div className="h-24 w-full animate-pulse rounded bg-rp-bone" />
            <div className="h-12 w-1/2 animate-pulse rounded bg-rp-bone" />
          </div>
        </div>
      </div>
    );
  }

  if (productError || !product) {
    return (
      <div className="rp-container flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
        <AlertCircle size={48} className="mb-4 text-rp-warn" />
        <h1 className="rp-section-h2 text-rp-ink">Product not found</h1>
        <p className="mt-2 max-w-md text-sm text-rp-moss">
          We couldn't locate that product. It may have been moved or removed.
        </p>
        <Link to="/products" className="rp-btn-primary mt-6">
          Browse all products <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const savings = product.compareAtPriceUsd
    ? product.compareAtPriceUsd - product.priceUsd
    : 0;

  return (
    <div className="min-h-full bg-rp-paper">
      {/* Breadcrumb */}
      <div className="border-b border-rp-line bg-rp-bone">
        <nav aria-label="Breadcrumb" className="rp-container py-3">
          <ol className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-rp-moss">
            <li>
              <Link to="/" className="hover:text-rp-forest hover:underline">
                Home
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight size={12} />
            </li>
            <li>
              <Link to="/products" className="hover:text-rp-forest hover:underline">
                Products
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight size={12} />
            </li>
            <li className="max-w-[200px] truncate text-rp-forest" aria-current="page">
              {product.name}
            </li>
          </ol>
        </nav>
      </div>

      {/* Product detail */}
      <section className="rp-container py-10 md:py-20">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid gap-8 md:grid-cols-2 lg:gap-16"
        >
          {/* Gallery */}
          <motion.div variants={fadeInUp} className="space-y-4">
            <div className="relative aspect-square overflow-hidden rounded-2xl border border-rp-line bg-rp-bone">
              <ProductImage product={product} className="transition-transform duration-500 hover:scale-105" />
              {savings > 0 && (
                <span className="absolute left-4 top-4 rounded-full bg-rp-warn px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                  Save {format(savings)}
                </span>
              )}
              {!product.inStock && (
                <span className="absolute right-4 top-4 rounded-full border border-rp-line bg-rp-paper/90 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-rp-moss backdrop-blur-sm">
                  Out of stock
                </span>
              )}
            </div>
            <div className="hidden gap-3 md:flex">
              {[1, 2, 3].map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`relative flex aspect-square w-20 items-center justify-center overflow-hidden rounded-xl border bg-rp-bone transition-colors ${
                    i === 1 ? 'border-rp-forest' : 'border-rp-line hover:border-rp-moss'
                  }`}
                >
                  <PenIllustration className="h-12 w-auto opacity-70" />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div variants={fadeInUp} className="flex flex-col">
            <span className="rp-mono-mini text-rp-moss">{product.category}</span>
            <h1 className="mt-2 text-h1 text-rp-ink">{product.name}</h1>
            <p className="mt-1 text-sm font-medium text-rp-moss">
              {product.dose}{product.form ? ` · ${product.form}` : ''}
            </p>

            {product.rating && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1 font-semibold text-rp-ink">
                  <Star size={14} className="fill-rp-warn text-rp-warn" />
                  {product.rating.score.toFixed(1)}
                </span>
                <span className="text-rp-moss">({product.rating.count} reviews)</span>
                {product.badges && product.badges.length > 0 && (
                  <span className="text-rp-moss">· {product.badges.join(' · ')}</span>
                )}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-1.5">
              {product.tags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-rp-line-2 bg-rp-bone px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-rp-moss"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex items-baseline gap-3">
              {product.priceFrom && <span className="text-lg text-rp-moss">From</span>}
              <span className="text-3xl font-bold tracking-tight text-rp-forest">{format(product.priceUsd)}</span>
              {product.compareAtPriceUsd && (
                <span className="text-lg text-rp-moss line-through">{format(product.compareAtPriceUsd)}</span>
              )}
            </div>

            <p className="mt-6 max-w-md text-sm leading-relaxed text-rp-ink">{product.description}</p>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <QuantitySelector quantity={quantity} onChange={setQuantity} />
              <button
                type="button"
                onClick={() => addItem(product.id, quantity)}
                disabled={!product.inStock}
                className="rp-btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ShoppingBag size={16} />
                {product.inStock ? 'Add to cart' : 'Out of stock'}
              </button>
            </div>

            <div className="mt-6">
              <TrustRow />
            </div>

            <div className="mt-8 rounded-2xl border border-rp-line bg-rp-bone p-5">
              <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-rp-forest">
                What's in the box
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-rp-ink">
                {[
                  'Lyophilized research peptide vial',
                  'Lot-sealed COA and HPLC certificate',
                  'Research-use statement and batch record',
                  'Secure cold-chain dispatch packaging',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 shrink-0 text-rp-leaf-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Details / specs */}
      <section className="border-y border-rp-line bg-rp-bone py-10 md:py-16">
        <div className="rp-container">
          <div className="grid gap-8 md:grid-cols-3">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.45 }}
              className="rounded-2xl border border-rp-line bg-rp-paper p-6"
            >
              <span className="rp-mono-mini text-rp-moss">Product</span>
              <h3 className="rp-section-h2 mt-2 text-rp-ink">About {product.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-rp-moss">{product.description}</p>
            </motion.div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="rounded-2xl border border-rp-line bg-rp-paper p-6"
            >
              <span className="rp-mono-mini text-rp-moss">Specifications</span>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between border-b border-rp-line-2 pb-2">
                  <dt className="text-rp-moss">Dose</dt>
                  <dd className="font-medium text-rp-ink">{product.dose}</dd>
                </div>
                <div className="flex justify-between border-b border-rp-line-2 pb-2">
                  <dt className="text-rp-moss">Format</dt>
                  <dd className="font-medium text-rp-ink">{product.form || 'Lyophilized'}</dd>
                </div>
                <div className="flex justify-between border-b border-rp-line-2 pb-2">
                  <dt className="text-rp-moss">Category</dt>
                  <dd className="font-medium text-rp-ink">{product.category}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-rp-moss">Availability</dt>
                  <dd className="font-medium text-rp-ink">{product.inStock ? 'In stock' : 'Out of stock'}</dd>
                </div>
              </dl>
            </motion.div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.45, delay: 0.16 }}
              className="rounded-2xl border border-rp-line bg-rp-paper p-6"
            >
              <span className="rp-mono-mini text-rp-moss">Shipping</span>
              <h3 className="rp-section-h2 mt-2 text-rp-ink">Dispatched next business day</h3>
              <ul className="mt-3 space-y-2 text-sm text-rp-moss">
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 shrink-0 text-rp-leaf-2" />
                  Free worldwide shipping
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 shrink-0 text-rp-leaf-2" />
                  DHL Express / FedEx International Priority
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 shrink-0 text-rp-leaf-2" />
                  Tracking link emailed within 24 hours
                </li>
                <li className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 shrink-0 text-rp-leaf-2" />
                  Cold-chain box with gel pack and indicator card
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related products */}
      <section className="rp-container py-10 md:py-20">
        <SectionHeader
          eyebrow="You may also like"
          title="Complete your protocol"
          description={`Other ${product.category.toLowerCase()} products that pair well with ${product.name}.`}
          align="left"
        />

        {relatedLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[5/6] animate-pulse rounded-2xl bg-rp-bone" />
            ))}
          </div>
        ) : relatedProducts.length > 0 ? (
          <motion.div
            initial={false}
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {relatedProducts.map((p) => (
              <motion.div key={p.id} variants={fadeInUp}>
                <ProductCard product={p} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="rounded-2xl border border-rp-line bg-rp-bone p-8 text-center">
            <p className="text-sm text-rp-moss">No related products found.</p>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="rp-btn-outline mt-4"
            >
              Browse all products <ArrowRight size={14} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
